import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { templateConfigApi } from '@/lib/api';
import { DEFAULT_TEMPLATE_SETTINGS, type HeaderField, type DocumentType } from '@/components/TemplateDesigner/types';

/**
 * Hook that fetches template custom fields for a given document type
 * and manages their per-document values.
 *
 * Convention: a field is "enabled" for this document if its key exists
 * in customFieldValues. Toggling off removes the key entirely.
 */
export function useCustomFields(
  businessId: string,
  documentType: DocumentType,
) {
  const [customFields, setCustomFields] = useState<HeaderField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);
  const populatedFromDoc = useRef(false);

  useEffect(() => {
    if (!businessId) return;
    templateConfigApi.get(businessId).then((r) => {
      if (r.data?.settings) {
        const settings = { ...DEFAULT_TEMPLATE_SETTINGS, ...r.data.settings };
        const fields = (settings.headerFields || []).filter(
          (f: HeaderField) => f.enabled && f.isCustom && f.documentTypes.includes(documentType)
        );
        setCustomFields(fields);
        // Pre-fill with defaults only for new documents (not populated from existing doc)
        if (!populatedFromDoc.current) {
          setCustomFieldValues((prev) => {
            if (Object.keys(prev).length > 0) return prev;
            const defaults: Record<string, string> = {};
            for (const field of fields) {
              defaults[field.id] = field.defaultValue || '';
            }
            return defaults;
          });
        }
        setLoaded(true);
      }
    }).catch(() => {});
  }, [businessId, documentType]);

  const setFieldValue = (fieldId: string, value: string) => {
    setCustomFieldValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const toggleField = (fieldId: string, enabled: boolean, defaultValue?: string) => {
    setCustomFieldValues((prev) => {
      if (enabled) {
        return { ...prev, [fieldId]: defaultValue || '' };
      } else {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      }
    });
  };

  // For populating from an existing document on edit.
  // Replaces state entirely — missing keys mean those fields are toggled off.
  const populateValues = (values: Record<string, string> | undefined | null) => {
    populatedFromDoc.current = true;
    setCustomFieldValues(values ?? {});
  };

  return { customFields, customFieldValues, setFieldValue, toggleField, populateValues, loaded };
}

/**
 * Renders editable inputs for template custom fields with per-document toggles.
 */
export function CustomFieldInputs({
  customFields,
  customFieldValues,
  setFieldValue,
  toggleField,
}: {
  customFields: HeaderField[];
  customFieldValues: Record<string, string>;
  setFieldValue: (fieldId: string, value: string) => void;
  toggleField: (fieldId: string, enabled: boolean, defaultValue?: string) => void;
}) {
  if (customFields.length === 0) return null;

  return (
    <div className="grid grid-cols-3 gap-4">
      {customFields.map((field) => {
        const isEnabled = field.id in customFieldValues;
        return (
          <div key={field.id} className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id={`cf-toggle-${field.id}`}
                checked={isEnabled}
                onCheckedChange={(checked) => toggleField(field.id, !!checked, field.defaultValue)}
              />
              <Label
                htmlFor={`cf-toggle-${field.id}`}
                className="text-muted-foreground cursor-pointer"
              >
                {field.name}
              </Label>
            </div>
            {isEnabled && (
              <Input
                value={customFieldValues[field.id] || ''}
                onChange={(e) => setFieldValue(field.id, e.target.value)}
                placeholder={field.defaultValue || field.name}
                className="bg-blue-500/5"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
